import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { empresasApi } from '../lib/api';
import { toast } from 'sonner';
import { 
  Building2, 
  Users, 
  CreditCard,
  Save,
  Trash2,
  Plus,
  Crown
} from 'lucide-react';

export default function ConfiguracionPage() {
  const { empresaActual, hasPermission, loadUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [empresa, setEmpresa] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [nuevoUsuarioEmail, setNuevoUsuarioEmail] = useState('');
  const [nuevoUsuarioRol, setNuevoUsuarioRol] = useState('VIEWER');

  useEffect(() => {
    const loadData = async () => {
      if (!empresaActual?.id) return;
      
      try {
        const [empresaRes, usuariosRes] = await Promise.all([
          empresasApi.getOne(empresaActual.id),
          empresasApi.getUsuarios(empresaActual.id).catch(() => ({ data: [] }))
        ]);
        setEmpresa(empresaRes.data);
        setUsuarios(usuariosRes.data);
      } catch (error) {
        console.error('Error loading config:', error);
        toast.error('Error al cargar la configuración');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [empresaActual?.id]);

  const handleSaveEmpresa = async () => {
    setSaving(true);
    try {
      await empresasApi.update(empresaActual.id, empresa);
      toast.success('Configuración guardada');
      loadUser(); // Reload user to update empresa data
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleAddUsuario = async () => {
    if (!nuevoUsuarioEmail) {
      toast.error('Introduce un email');
      return;
    }
    
    try {
      await empresasApi.addUsuario(empresaActual.id, nuevoUsuarioEmail, nuevoUsuarioRol);
      toast.success('Usuario añadido');
      setNuevoUsuarioEmail('');
      // Reload usuarios
      const res = await empresasApi.getUsuarios(empresaActual.id);
      setUsuarios(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al añadir usuario');
    }
  };

  const handleRemoveUsuario = async (usuarioId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
    
    try {
      await empresasApi.removeUsuario(empresaActual.id, usuarioId);
      toast.success('Usuario eliminado');
      setUsuarios(usuarios.filter(u => u.id !== usuarioId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const isOwner = hasPermission(['OWNER']);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!empresa) return null;

  const planInfo = {
    FREE: { name: 'Starter', color: 'bg-slate-100 text-slate-700' },
    PRO: { name: 'Profesional', color: 'bg-blue-100 text-blue-700' },
    BUSINESS: { name: 'Equipos', color: 'bg-violet-100 text-violet-700' }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn" data-testid="configuracion-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Configuración
          </h1>
          <p className="text-slate-500 mt-1">
            Gestiona la configuración de tu empresa
          </p>
        </div>

        <Tabs defaultValue="empresa" className="space-y-6">
          <TabsList>
            <TabsTrigger value="empresa" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Empresa
            </TabsTrigger>
            {isOwner && (
              <TabsTrigger value="usuarios" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Usuarios
              </TabsTrigger>
            )}
            <TabsTrigger value="plan" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Plan
            </TabsTrigger>
          </TabsList>

          {/* Empresa Tab */}
          <TabsContent value="empresa">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Datos de la Empresa</CardTitle>
                <CardDescription>
                  Esta información aparecerá en tus presupuestos
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="nombre_comercial">Nombre Comercial *</Label>
                    <Input
                      id="nombre_comercial"
                      value={empresa.nombre_comercial || ''}
                      onChange={(e) => setEmpresa({ ...empresa, nombre_comercial: e.target.value })}
                      disabled={!isOwner}
                      data-testid="config-nombre-comercial"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="razon_social">Razón Social</Label>
                    <Input
                      id="razon_social"
                      value={empresa.razon_social || ''}
                      onChange={(e) => setEmpresa({ ...empresa, razon_social: e.target.value })}
                      disabled={!isOwner}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cif_nif">CIF/NIF</Label>
                    <Input
                      id="cif_nif"
                      value={empresa.cif_nif || ''}
                      onChange={(e) => setEmpresa({ ...empresa, cif_nif: e.target.value })}
                      disabled={!isOwner}
                      data-testid="config-cif"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="telefono">Teléfono</Label>
                    <Input
                      id="telefono"
                      value={empresa.telefono || ''}
                      onChange={(e) => setEmpresa({ ...empresa, telefono: e.target.value })}
                      disabled={!isOwner}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="direccion">Dirección</Label>
                  <Input
                    id="direccion"
                    value={empresa.direccion || ''}
                    onChange={(e) => setEmpresa({ ...empresa, direccion: e.target.value })}
                    disabled={!isOwner}
                    data-testid="config-direccion"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="logo_url">URL del Logo</Label>
                  <Input
                    id="logo_url"
                    value={empresa.logo_url || ''}
                    onChange={(e) => setEmpresa({ ...empresa, logo_url: e.target.value })}
                    placeholder="https://ejemplo.com/logo.png"
                    disabled={!isOwner}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="terminos">Términos y Condiciones por Defecto</Label>
                  <Textarea
                    id="terminos"
                    value={empresa.terminos_condiciones || ''}
                    onChange={(e) => setEmpresa({ ...empresa, terminos_condiciones: e.target.value })}
                    rows={4}
                    disabled={!isOwner}
                    data-testid="config-terminos"
                  />
                  <p className="text-sm text-slate-500">
                    Estos términos se incluirán por defecto en todos tus presupuestos
                  </p>
                </div>

                {isOwner && (
                  <Button 
                    onClick={handleSaveEmpresa} 
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={saving}
                    data-testid="config-save-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Usuarios Tab */}
          {isOwner && (
            <TabsContent value="usuarios">
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Gestión de Usuarios</CardTitle>
                  <CardDescription>
                    Añade o elimina usuarios de tu empresa
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Add User Form */}
                  <div className="flex gap-4 p-4 bg-slate-50 rounded-lg">
                    <Input
                      placeholder="Email del usuario"
                      value={nuevoUsuarioEmail}
                      onChange={(e) => setNuevoUsuarioEmail(e.target.value)}
                      className="flex-1"
                      data-testid="add-user-email"
                    />
                    <Select value={nuevoUsuarioRol} onValueChange={setNuevoUsuarioRol}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MANAGER">Manager</SelectItem>
                        <SelectItem value="VIEWER">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={handleAddUsuario} className="bg-blue-600 hover:bg-blue-700" data-testid="add-user-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Añadir
                    </Button>
                  </div>

                  {/* Users Table */}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Usuario</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Rol</TableHead>
                        <TableHead className="w-[70px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {usuarios.map((usuario) => (
                        <TableRow key={usuario.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {usuario.nombre}
                              {usuario.rol === 'OWNER' && (
                                <Crown className="w-4 h-4 text-amber-500" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{usuario.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{usuario.rol}</Badge>
                          </TableCell>
                          <TableCell>
                            {usuario.rol !== 'OWNER' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleRemoveUsuario(usuario.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Plan Tab */}
          <TabsContent value="plan">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Tu Plan Actual</CardTitle>
                <CardDescription>
                  Gestiona tu suscripción y límites
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-4 p-6 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {planInfo[empresa.plan]?.name || empresa.plan}
                      </h3>
                      <Badge className={planInfo[empresa.plan]?.color}>
                        Activo
                      </Badge>
                    </div>
                    <p className="text-slate-500 mt-1">
                      {empresa.plan === 'FREE' && 'Plan gratuito con funcionalidades básicas'}
                      {empresa.plan === 'PRO' && 'Plan profesional con todas las funcionalidades'}
                      {empresa.plan === 'BUSINESS' && 'Plan empresarial con funcionalidades avanzadas'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-white border rounded-lg">
                    <p className="text-sm text-slate-500">Usuarios</p>
                    <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {usuarios.length} / {empresa.plan === 'FREE' ? '1' : empresa.plan === 'PRO' ? '5' : '∞'}
                    </p>
                  </div>
                  <div className="p-4 bg-white border rounded-lg">
                    <p className="text-sm text-slate-500">Presupuestos activos</p>
                    <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      - / {empresa.plan === 'FREE' ? '5' : '∞'}
                    </p>
                  </div>
                  <div className="p-4 bg-white border rounded-lg">
                    <p className="text-sm text-slate-500">Multi-empresa</p>
                    <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {empresa.plan === 'BUSINESS' ? 'Sí' : 'No'}
                    </p>
                  </div>
                </div>

                {empresa.plan !== 'BUSINESS' && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-900">¿Necesitas más?</h4>
                    <p className="text-blue-700 text-sm mt-1">
                      Actualiza tu plan para desbloquear más funcionalidades y eliminar límites.
                    </p>
                    <Button className="mt-3 bg-blue-600 hover:bg-blue-700">
                      Ver Planes
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
